import * as API from '../api'
import * as Store from '../store'
import { debounce, merge } from 'lodash'
import waitUntil from 'async-wait-until'
import sleep from '@/helpers/sleep'
import store from '@/core/store'
import WritingModalService from '../../writing/WritingModal.service'
import writingService from '../writing.service'
import ToastService from '@/components/Toast/Toast.service'
import threadsService from '@/app-domains/threads/threads.service'
import boxesService from '@/app-domains/boxes/boxes.service'

class DraftManagerService {
  constructor () {
    this.isDraftBeingCreated = false
    this.debouncedStoreUpdate = debounce((data) => Store.setActiveDraftValues(data), 80)
    this.debouncedUpdate = debounce(this.directUpdate, 500)
    this.debouncedAPIUpdate = debounce(this.directUpdate, 700)
    this.longDebouncedAPIUpdate = debounce(this.directUpdate, 5000)
  }

  /** DRAFT MESSAGE CREATION */

  isCurrentDraftCreated () {
    return !!this.getCurrentDraftMessageFromStore()
  }

  async createDraftMessage (type = 'standalone', data) {
    this.isDraftBeingCreated = true
    let response
    if (data) {
      const { parentMessageId, ...draftData } = data
      response = await API.createDraftMessage(type, parentMessageId, draftData)
    } else {
      response = await API.createDraftMessage(type)
    }
    this.isDraftBeingCreated = false
    return response
  }

  async createDraftMessageAndStore (parentMessageId = undefined) {
    const type = Store.getDraftType()
    const draftMessage = await this.createDraftMessage(type, { parentMessageId })
    Store.storeDraftOnListAndMarkAsActive(draftMessage)
    // Storing temporal draft values on the actual message just in case there are new ones
    Store.storeTemporalDraftValuesOnActiveDraft()
    // FIXME: We should be able to send the proper parameters
    // on the createDraftMessage method instead of syncing after creation
    await this.updateDraftMessage()
  }

  async createDraftIfNoActiveOneExists () {
    if (!this.isCurrentDraftCreated()) {
      if (this.isDraftBeingCreated) {
        await waitUntil(() => this.isCurrentDraftCreated() && !this.isDraftBeingCreated, { timeout: 45000 })
        await sleep(50) // Avoiding errors meanwhile dispatching
        return null
      }
      const parentMessageId = Store.getTemporalDraftValues()?.parentMessageId
      await this.createDraftMessageAndStore(parentMessageId)
    }
  }
  
  /** DRAFT MESSAGE GET */

  async getLastDrafts (page = 0, retrieveFromStore = false) {
    if (retrieveFromStore) {
      return Store.getLastDraftsList()
    }
    const lastDrafts = await API.getLastDrafts(page)
    const parsedLastDrafts = Store.storeDraftsOnList(lastDrafts.results)
    return parsedLastDrafts
  }

  getTemporalDraftForParentMessage (message) {
    const temporalDraft = Store.getTemporalDraft()
    return temporalDraft?.parentMessageId === message.messageid ? temporalDraft : undefined
  }

  getStoreDraftDataByParentMessage (parentMessage, thread, defaultDomain, type = 'reply') {
    if (this.replyDraftForParentMessageExists(parentMessage)) {
      return this.getReplyDraftForParentMessage(parentMessage)
    } else if (this.isDraftBeingCreated) { // TODO: Add check if the draft is being created for the same parent id
      return this.getTemporalDraftForParentMessage(parentMessage)
    } else {
      this.setCurrentDraftValuesBeforeCreationFromParentMessage(parentMessage, thread, defaultDomain, type)
      return this.getTemporalDraftForParentMessage(parentMessage)
    }
  }

  replyDraftForParentMessageExists (message) {
    return Store.getCurrentDraft()?.parentMessageId === message.messageid
  }

  getReplyDraftForParentMessage (message) {
    const draftListObject = Store.getLastDraftsList()
    const filteredValues = Object.values(draftListObject).filter(
      (draft) => draft.type === 'reply' && draft.parentMessageId === message.messageid
    )

    return filteredValues.length > 0 ? filteredValues[0] : undefined
  }

  getStoredLastDrafts () {
    return Store.getLastDraftsList()
  }

  preventFeedFromBeingEmpty () {
    if (Store.getLastDraftsList().length < 5) { this.getLastDrafts(0) }
  }

  /** DRAFT MESSAGE UPDATE */

  async updateOrCreateDraft (type, data) {
    if (this.isCurrentDraftCreated(type)) {
      Store.setCurrentDraft(data)
      await this.updateCurrentDraft(data)
    } else if (this.isDraftBeingCreated) {
      Store.saveTemporalDraftValues(data)
    } else {
      data = merge(data, Store.getTemporalDraftValues())
      Store.saveTemporalDraftValues(data)
      data = merge(data, Store.getTemporalDraftValues())
      await this.createDraftMessageAndStore(data.parentMessageId)
    }
  }

  async switchDraftMessage (messageId) {
    this.updateDraftMessage()
    Store.switchDraftMessage(messageId)
  }

  async directUpdate () {
    const currentDraft = Store.getCurrentDraftFromList()
    if (currentDraft) {
      await API.updateDraftMessage(currentDraft.id, currentDraft)
    }
  }

  async updateDraftMessage () {
    if (this.isDraftReadyToBeSent(Store.getCurrentDraft())) {
      store.commit('DRAFT_MESSAGE:READY_TO_SEND', true)
    } else {
      store.commit('DRAFT_MESSAGE:READY_TO_SEND', false)
    }
    await this.debouncedAPIUpdate()
  }

  async updateCurrentDraft (data) {
    this.debouncedStoreUpdate(data)
    await this.updateDraftMessage()
  }

  areThereAnyReplyTos (headers) {
    for (const key in headers) {
      if (key.toLowerCase().includes('reply-to')) {
        // Add here check in case it contains "text" <mail>
        // eslint-disable-next-line
        const validEmailsRegexp = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi
        const matches = headers[key].match(validEmailsRegexp)
        if (!matches) {
          return undefined
        }
        return matches?.length > 1 ? matches[1] : undefined
      }
    }
  }

  setCurrentDraftValuesBeforeCreationFromParentMessage (message, thread, defaultDomain, type) {
    const replyToValue = this.areThereAnyReplyTos(message.headers)
    const from = type === 'reply' ? message?.boxName : ''
    const tos = type === 'reply' ? (replyToValue ? [replyToValue] : thread.participants.filter((participant) => participant.split('@')[1] !== defaultDomain)) : []
    const ccs = []
    const bccs = []
    const parentMessageId = message?.messageid
    const subject = writingService.buildSubject(message.subject, type)

    const temporalValues = {
      from,
      tos,
      ccs,
      bccs,
      parentMessageId,
      subject,
      type,
      additionalBody: message.html
    }

    Store.saveTemporalDraftValues(temporalValues)

    return temporalValues
  }

  /** DRAFT MESSAGE DELETE */

  async deleteCurrentDraft () {
    const draftId = Store.getCurrentDraftFromList()?.id
    await this.deleteDraftByMessageId(draftId)
  }

  async deleteDraftByMessageId (messageId) {
    const currentDraftId = Store.getCurrentDraft()?.id
    Store.removeDraftMessage(messageId)
    ToastService.show({
      type: 'info',
      title: 'Deleting',
      message: 'Deleting draft message ...',
    })
    await API.deleteMessageDraft(messageId)
    this.preventFeedFromBeingEmpty()
    ToastService.show({
      type: 'success',
      title: 'Success!',
      message: 'Draft message deleted',
    })
    
    this.unSetActiveDraft()
    if (messageId === currentDraftId) {
      this.cleanDraft()
    }

    // FIXME: Maybe it's not needed to get last drafts everytime they are deleted
    await this.getLastDrafts()
  }

  // FIXME: Add API delete here!
  discardReplyDraft (message) {
    const reply = this.getReplyDraftForParentMessage(message)
    this.switchDraftMessage(reply.id)
    this.deleteCurrentDraft()
  }

  isDraftReadyToBeSent (message) {
    if (!message) return false
    if (!(Array.isArray(message.tos) && message.tos.length > 0)) { store.commit('DRAFT_MESSAGE:VALIDATION_ERROR:TOS') }
    if (!message.from) {
      store.commit('DRAFT_MESSAGE:VALIDATION_ERROR:FROM')
    }
    return Array.isArray(message.tos) && message.tos.length > 0 && message.from
  }

  /** MESSAGE SENDING */

  async sendMessageInstantly (parentMessageId) {
    try {
      const draftMessage = this.getCurrentDraftMessageFromStore()
      await this.directUpdate()
      if (!this.isDraftReadyToBeSent(draftMessage)) {
        return
      }
      const message = await this.sendMail(parentMessageId)
      ToastService.show({
        type: 'info',
        title: 'Sending!',
        message: 'Message is being sent',
      })
      boxesService.getUserBoxes()
      threadsService.queryLastThreads({ page: 0 }, false)
      Store.resetStandaloneMessage()
      Store.removeDraftMessage(draftMessage.id)
      ToastService.show({
        type: 'success',
        title: 'Success!',
        message: 'Message was sent',
      })
      if (parentMessageId) {
        store.commit(`DRAFT_MESSAGE:${parentMessageId}:SENT`)
      }
      WritingModalService.close()
      /*
        Used for telling the users their first message was sent
        if (getItemFromLocalStorage('onboarding:firstMessage')) {
          removeItemFromLocalStorage('onboarding:firstMessage')
          modalService.open(<MessageSentModal/>)
        }
      */
      
      return message
    } catch (e) {
      ToastService.show({
        type: 'error',
        title: 'Error!',
        message: 'There was an error while sending the message',
      })
      throw new Error(e)
    }
  }

  async sendMail (parentMessageId = '') {
    const messageToSend = Store.getCurrentDraft()

    if (parentMessageId.length > 0 && messageToSend.parentMessageId !== parentMessageId) {
      throw new Error('No reply active matching that parentMessageId')
    }

    const message = await API.sendStandaloneMessage(messageToSend.id)
    if (parentMessageId) {
      store.commit(`DRAFT_MESSAGE:${parentMessageId}:SENT`)
    }
    store.commit('STANDALONE_MESSAGE:SENT')
    return message
  }

  /** PURE STORE SETTERS/GETTERS */
  
  setDraftType (type) {
    Store.setDraftType(type)
  }

  cleanDraft () {
    Store.resetStandaloneMessage()
  }
  
  unSetActiveDraft () {
    Store.unSetActiveDraft()
  }

  getCurrentDraftMessageFromStore () {
    return Store.getCurrentDraftFromList()
  }

  getStoredDraftMessage () {
    if (Store.getCurrentDraftFromList()?.id) {
      const currentDraft = Store.getCurrentDraft()
      return currentDraft
    }
  }

  getLastUpdatedDraftMessageFromStore (type = 'standalone') {
    const draftsList = Store.getLastDraftsList()
    const lastDraftMessage = Object.values(draftsList)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .filter((draft) => draft.type === type)[0]

    Store.markDraftAsActive(lastDraftMessage)
    return lastDraftMessage
  }
}

export default new DraftManagerService()
